package com.schoolconnect.app.repository.classsubjectlink

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.ClassSubjectLinkCreateRequest
import com.schoolconnect.app.datasource.network.response.ClassSubjectLinkResponse
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.repository.subject.SubjectRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

sealed interface ClassSubjectLinkScope {
    data object All : ClassSubjectLinkScope
    data class School(val schoolId: String) : ClassSubjectLinkScope
    data class AcademicYear(val academicYearId: String) : ClassSubjectLinkScope
    data class ClassSection(val classSectionId: String) : ClassSubjectLinkScope
}

object ClassSubjectLinkRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ClassSubjectLinkRepository"
    private const val PAGE_SIZE = 100
    private const val MAX_PAGES = 100

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class CachedSubjectCodesBySection(
        val schoolId: String,
        val classSectionId: String,
        val codes: List<String>
    )

    private data class CachedSubjectLinksBySection(
        val schoolId: String,
        val classSectionId: String,
        val links: List<ClassSubjectLinkResponse>
    )

    private var cachedSubjectCodesBySection: CachedSubjectCodesBySection? = null
    private var cachedSubjectLinksBySection: CachedSubjectLinksBySection? = null

    suspend fun loadClassSectionSubjectLinks(
        schoolId: String,
        classSectionId: String,
        forceRemote: Boolean = false
    ): Result<List<ClassSubjectLinkResponse>> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedClassSectionId = classSectionId.trim()
        if (normalizedSchoolId.isBlank() || normalizedClassSectionId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId and classSectionId are required"))
        }

        cachedSubjectLinksBySection?.takeIf {
            !forceRemote &&
                it.schoolId == normalizedSchoolId &&
                it.classSectionId == normalizedClassSectionId
        }?.let { cache ->
            scLog(
                "$TAG loadClassSectionSubjectLinks cacheHit school=$normalizedSchoolId classSectionId=$normalizedClassSectionId count=${cache.links.size}"
            )
            return Result.success(cache.links)
        }

        scLog(
            "$TAG loadClassSectionSubjectLinks start school=$normalizedSchoolId classSectionId=$normalizedClassSectionId forceRemote=$forceRemote"
        )
        return runCatching {
            val links = loadPagedItems(
                tag = TAG,
                operation = "loadClassSectionSubjectLinksRows",
                pageSize = PAGE_SIZE,
                maxPages = MAX_PAGES,
                startContext = "school=$normalizedSchoolId classSectionId=$normalizedClassSectionId"
            ) { offset ->
                KtorApiClient.getSubjectsTaughtInSection(
                    classSectionId = normalizedClassSectionId,
                    limit = PAGE_SIZE,
                    offset = offset
                ).getOrElse { throwable ->
                    scLog(
                        "$TAG loadClassSectionSubjectLinks pageFailure school=$normalizedSchoolId classSectionId=$normalizedClassSectionId offset=$offset",
                        throwable
                    )
                    throw throwable
                }
            }.distinctBy { it.id }

            cachedSubjectLinksBySection = CachedSubjectLinksBySection(
                schoolId = normalizedSchoolId,
                classSectionId = normalizedClassSectionId,
                links = links
            )
            scLog(
                "$TAG loadClassSectionSubjectLinks complete school=$normalizedSchoolId classSectionId=$normalizedClassSectionId count=${links.size}"
            )
            links
        }.onFailure { throwable ->
            scLog(
                "$TAG loadClassSectionSubjectLinks failure school=$normalizedSchoolId classSectionId=$normalizedClassSectionId",
                throwable
            )
        }
    }

    suspend fun loadClassSectionSubjectCodes(
        schoolId: String,
        classSectionId: String,
        forceRemote: Boolean = false,
        subjectCodeMap: Map<String, String>? = null
    ): Result<List<String>> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedClassSectionId = classSectionId.trim()
        if (normalizedSchoolId.isBlank() || normalizedClassSectionId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId and classSectionId are required"))
        }

        cachedSubjectCodesBySection?.takeIf {
            !forceRemote &&
                it.schoolId == normalizedSchoolId &&
                it.classSectionId == normalizedClassSectionId
        }?.let { cache ->
            scLog(
                "$TAG loadClassSectionSubjectCodes cacheHit school=$normalizedSchoolId classSectionId=$normalizedClassSectionId count=${cache.codes.size}"
            )
            return Result.success(cache.codes)
        }

        scLog(
            "$TAG loadClassSectionSubjectCodes start school=$normalizedSchoolId classSectionId=$normalizedClassSectionId forceRemote=$forceRemote"
        )
        return runCatching {
            val links = loadPagedItems(
                tag = TAG,
                operation = "loadClassSectionSubjectCodesLinks",
                pageSize = PAGE_SIZE,
                maxPages = MAX_PAGES,
                startContext = "school=$normalizedSchoolId classSectionId=$normalizedClassSectionId"
            ) { offset ->
                KtorApiClient.getSubjectsTaughtInSection(
                    classSectionId = normalizedClassSectionId,
                    limit = PAGE_SIZE,
                    offset = offset
                ).getOrElse { throwable ->
                    scLog(
                        "$TAG loadClassSectionSubjectCodes pageFailure school=$normalizedSchoolId classSectionId=$normalizedClassSectionId offset=$offset",
                        throwable
                    )
                    throw throwable
                }
            }.distinctBy { it.subjectId.orEmpty() }

            val subjectIds = links.mapNotNull { it.subjectId?.takeIf { subjectId -> subjectId.isNotBlank() } }
            if (subjectIds.isEmpty()) {
                cachedSubjectCodesBySection = CachedSubjectCodesBySection(
                    schoolId = normalizedSchoolId,
                    classSectionId = normalizedClassSectionId,
                    codes = emptyList()
                )
                return@runCatching emptyList<String>()
            }

            val resolvedSubjectCodeMap = subjectCodeMap ?: SubjectRepository.loadSchoolSubjectShortCodeMap(
                schoolId = normalizedSchoolId,
                forceRemote = forceRemote
            ).getOrElse { throwable ->
                scLog(
                    "$TAG loadClassSectionSubjectCodes subjectMapFailure school=$normalizedSchoolId classSectionId=$normalizedClassSectionId",
                    throwable
                )
                throw throwable
            }

            val subjectCodes = subjectIds.mapNotNull { resolvedSubjectCodeMap[it]?.trim() }.filter { it.isNotBlank() }.distinct()

            cachedSubjectCodesBySection = CachedSubjectCodesBySection(
                schoolId = normalizedSchoolId,
                classSectionId = normalizedClassSectionId,
                codes = subjectCodes
            )
            scLog(
                "$TAG loadClassSectionSubjectCodes complete school=$normalizedSchoolId classSectionId=$normalizedClassSectionId count=${subjectCodes.size}"
            )
            subjectCodes
        }.onFailure { throwable ->
            scLog(
                "$TAG loadClassSectionSubjectCodes failure school=$normalizedSchoolId classSectionId=$normalizedClassSectionId",
                throwable
            )
        }
    }

    suspend fun loadClassSectionSubjectNames(
        schoolId: String,
        classSectionId: String,
        forceRemote: Boolean = false
    ): Result<List<String>> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedClassSectionId = classSectionId.trim()
        if (normalizedSchoolId.isBlank() || normalizedClassSectionId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId and classSectionId are required"))
        }
        return runCatching {
            val links = loadClassSectionSubjectLinks(
                normalizedSchoolId,
                normalizedClassSectionId,
                forceRemote
            ).getOrElse { throw it }
            val options = SubjectRepository.loadSchoolSubjectOptions(
                normalizedSchoolId,
                forceRemote
            ).getOrElse { throw it }
            links.asSequence()
                .filter { it.isActive != false }
                .mapNotNull { link ->
                    val code = options.codeById[link.subjectId.orEmpty()]?.uppercase()
                    code?.let(options.nameByCode::get)
                }
                .map(String::trim)
                .filter(String::isNotBlank)
                .distinct()
                .sortedBy(String::lowercase)
                .toList()
        }.onFailure { throwable ->
            scLog(
                "$TAG loadClassSectionSubjectNames failure school=$normalizedSchoolId classSectionId=$normalizedClassSectionId",
                throwable
            )
        }
    }

    suspend fun createClassSubjectLink(
        request: ClassSubjectLinkCreateRequest
    ): Result<ClassSubjectLinkResponse> {
        val normalizedClassSectionId = request.classSectionId.trim()
        val normalizedSubjectId = request.subjectId.trim()
        if (normalizedClassSectionId.isBlank() || normalizedSubjectId.isBlank()) {
            return Result.failure(IllegalArgumentException("classSectionId and subjectId are required"))
        }
        scLog(
            "$TAG createClassSubjectLink start classSectionId=$normalizedClassSectionId subjectId=$normalizedSubjectId"
        )
        return KtorApiClient.createClassSubjectLink(
            request.copy(
                classSectionId = normalizedClassSectionId,
                subjectId = normalizedSubjectId
            )
        ).onSuccess {
            invalidateCaches()
            scLog(
                "$TAG createClassSubjectLink success classSectionId=$normalizedClassSectionId subjectId=$normalizedSubjectId id=${it.id.orEmpty()}"
            )
        }.onFailure { throwable ->
            scLog(
                "$TAG createClassSubjectLink failure classSectionId=$normalizedClassSectionId subjectId=$normalizedSubjectId",
                throwable
            )
        }
    }

    suspend fun deleteClassSubjectLink(classSubjectLinkId: String): Result<Unit> {
        val normalizedId = classSubjectLinkId.trim()
        if (normalizedId.isBlank()) {
            return Result.failure(IllegalArgumentException("classSubjectLinkId is required"))
        }
        scLog("$TAG deleteClassSubjectLink start classSubjectLinkId=$normalizedId")
        return KtorApiClient.deleteClassSubjectLink(normalizedId).onSuccess {
            invalidateCaches()
            scLog("$TAG deleteClassSubjectLink success classSubjectLinkId=$normalizedId")
        }.onFailure { throwable ->
            scLog("$TAG deleteClassSubjectLink failure classSubjectLinkId=$normalizedId", throwable)
        }
    }

    fun invalidateCaches() {
        cachedSubjectCodesBySection = null
        cachedSubjectLinksBySection = null
    }
}
